import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../api';

function WhatsAppStatus() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduled, setScheduled] = useState([]);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  
  // Configuration form state
  const [connectionType, setConnectionType] = useState('baileys');
  const [apiKey, setApiKey] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('https://api.wavo.digital');
  const [configSaving, setConfigSaving] = useState(false);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadScheduled();
    
    // Poll status every 5 seconds
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      const response = await whatsappAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await whatsappAPI.getConfig();
      setConfig(response.data);
      setConnectionType(response.data.connection_type || 'baileys');
      setApiBaseUrl(response.data.api_base_url || 'https://api.wavo.digital');
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadScheduled = async () => {
    try {
      const response = await whatsappAPI.getScheduled();
      setScheduled(response.data);
    } catch (error) {
      console.error('Error loading scheduled messages:', error);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setConfigSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await whatsappAPI.updateConfig({
        connectionType,
        apiKey: apiKey || null,
        apiBaseUrl
      });
      
      setSuccess('Configuration saved successfully!');
      setShowConfig(false);
      await loadConfig();
      await loadStatus();
    } catch (error) {
      setError('Failed to save configuration: ' + (error.response?.data?.error || error.message));
    } finally {
      setConfigSaving(false);
    }
  };

  const handleTestBusinessApi = async () => {
    setError('');
    setSuccess('');
    
    if (!apiKey) {
      setError('Please enter an API key first');
      return;
    }
    
    try {
      const response = await whatsappAPI.testBusinessApi(apiKey, apiBaseUrl);
      setSuccess(response.data.message || 'Business API connection successful!');
    } catch (error) {
      setError('Connection test failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await whatsappAPI.sendTest(testPhone, testMessage);
      setSuccess('Test message sent successfully!');
      setTestPhone('');
      setTestMessage('');
    } catch (error) {
      setError('Failed to send test message: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout from WhatsApp?')) {
      try {
        await whatsappAPI.logout();
        setSuccess('Logged out successfully. A new QR code should appear shortly.');
        setTimeout(() => loadStatus(), 2000);
      } catch (error) {
        setError('Failed to logout');
      }
    }
  };

  const handleForceReconnect = async () => {
    if (window.confirm('This will clear the current session and generate a new QR code. Continue?')) {
      try {
        await whatsappAPI.logout();
        setSuccess('Session cleared. Waiting for new QR code...');
        setTimeout(() => loadStatus(), 2000);
      } catch (error) {
        setError('Failed to clear session.');
      }
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading WhatsApp status...</div></div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>WhatsApp Connection</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => setShowConfig(!showConfig)}
        >
          ⚙️ Configuration
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card" style={{ background: '#f8f9fa', border: '2px solid #007bff' }}>
          <h2>📡 WhatsApp Configuration</h2>
          <form onSubmit={handleSaveConfig}>
            <div className="form-group">
              <label><strong>Connection Type</strong></label>
              <div style={{ marginTop: '10px' }}>
                <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="baileys"
                    checked={connectionType === 'baileys'}
                    onChange={(e) => setConnectionType(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>Personal Number (Baileys)</strong>
                  <div style={{ marginLeft: '26px', fontSize: '13px', color: '#666' }}>
                    Use your personal WhatsApp number with QR code scanning
                  </div>
                </label>
              </div>
              <div style={{ marginTop: '15px' }}>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="business_api"
                    checked={connectionType === 'business_api'}
                    onChange={(e) => setConnectionType(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>Business API (Wavo)</strong>
                  <div style={{ marginLeft: '26px', fontSize: '13px', color: '#666' }}>
                    Use WhatsApp Business API with approved templates
                  </div>
                </label>
              </div>
            </div>

            {connectionType === 'business_api' && (
              <>
                <div className="form-group">
                  <label><strong>API Key</strong></label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="wavo_live_your_api_key_here"
                    required={connectionType === 'business_api'}
                  />
                  <small style={{ color: '#666' }}>
                    Get your API key from Wavo Dashboard → Settings → API Tokens
                  </small>
                </div>

                <div className="form-group">
                  <label><strong>API Base URL</strong></label>
                  <input
                    type="text"
                    value={apiBaseUrl}
                    onChange={(e) => setApiBaseUrl(e.target.value)}
                    placeholder="https://api.wavo.digital"
                  />
                </div>

                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleTestBusinessApi}
                  style={{ marginRight: '10px' }}
                >
                  Test Connection
                </button>
              </>
            )}

            <div style={{ marginTop: '20px' }}>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={configSaving}
                style={{ marginRight: '10px' }}
              >
                {configSaving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfig(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Current Mode Badge */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Connection Status</h2>
          <span style={{ 
            padding: '6px 12px', 
            borderRadius: '20px', 
            background: status?.type === 'business_api' ? '#28a745' : '#007bff',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {status?.type === 'business_api' ? '🏢 Business API' : '📱 Personal Number'}
          </span>
        </div>
        
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {status?.type === 'business_api' ? (
            <div>
              <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '10px' }}>✓</div>
              <h3 style={{ color: '#28a745' }}>Business API Connected</h3>
              <p>Using Wavo WhatsApp Business API</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                Ready to send campaigns with approved templates
              </p>
            </div>
          ) : status?.isConnected ? (
            <div>
              <div style={{ fontSize: '48px', color: '#25d366', marginBottom: '10px' }}>✓</div>
              <h3 style={{ color: '#25d366' }}>Connected</h3>
              <p>WhatsApp is connected and ready to send messages</p>
              {status.phoneNumber && (
                <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '15px 0', color: '#00a0dc' }}>
                  📱 +{status.phoneNumber}
                </p>
              )}
              <button className="btn btn-danger" onClick={handleLogout} style={{ marginTop: '15px' }}>
                Logout
              </button>
            </div>
          ) : status?.state === 'qr' && status?.qrCode ? (
            <div className="qr-container">
              <h3 style={{ color: '#2196f3', marginBottom: '10px' }}>Scan QR Code with WhatsApp</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>Open WhatsApp on your phone and scan this QR code</p>
              <div style={{ 
                display: 'inline-block',
                padding: '20px', 
                background: '#fff', 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                <img 
                  src={status.qrCode} 
                  alt="WhatsApp QR Code" 
                  style={{ 
                    width: '300px', 
                    height: '300px',
                    display: 'block'
                  }} 
                />
              </div>
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#e3f2fd', 
                borderRadius: '8px',
                maxWidth: '400px',
                margin: '20px auto 0'
              }}>
                <p style={{ color: '#1976d2', fontWeight: 'bold', marginBottom: '8px' }}>
                  📱 How to scan:
                </p>
                <p style={{ color: '#424242', fontSize: '14px', margin: 0 }}>
                  1. Open WhatsApp on your phone<br/>
                  2. Tap Menu (⋮) or Settings → Linked Devices<br/>
                  3. Tap "Link a Device"<br/>
                  4. Point your phone at this screen to scan the code
                </p>
              </div>
            </div>
          ) : status?.state === 'connecting' ? (
            <div>
              <div style={{ fontSize: '48px', color: '#ff9800', marginBottom: '10px' }}>🔄</div>
              <h3 style={{ color: '#ff9800' }}>Connecting...</h3>
              <p>Initializing WhatsApp connection. Please wait...</p>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '48px', color: '#dc3545', marginBottom: '10px' }}>✗</div>
              <h3 style={{ color: '#dc3545' }}>Disconnected</h3>
              <p>WhatsApp is disconnected. Waiting for QR code to appear...</p>
              <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                If the QR code doesn't appear within a few seconds, try forcing a new connection.
              </p>
              <button 
                className="btn btn-primary" 
                onClick={handleForceReconnect} 
                style={{ marginTop: '15px' }}
              >
                Generate New QR Code
              </button>
            </div>
          )}
        </div>
      </div>

      {(status?.isConnected || status?.type === 'business_api') && (
        <div className="card">
          <h2>Send Test Message</h2>
          <form onSubmit={handleSendTest}>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="text"
                required
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="e.g., 5511999999999"
              />
              <small style={{ color: '#666' }}>Include country code without +</small>
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                required
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Enter test message..."
              />
            </div>
            <button type="submit" className="btn btn-primary">Send Test</button>
          </form>
        </div>
      )}

      <div className="card">
        <h2>Scheduled Messages</h2>
        {scheduled.length === 0 ? (
          <p>No scheduled messages</p>
        ) : (
          <div className="table-container">
            <table className="table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Scheduled Time</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {scheduled.map(schedule => (
                <tr key={schedule.id}>
                  <td>{schedule.campaign_name}</td>
                  <td>{new Date(schedule.scheduled_time).toLocaleString()}</td>
                  <td>
                    <span className={`badge badge-${
                      schedule.status === 'pending' ? 'warning' :
                      schedule.status === 'completed' ? 'success' :
                      schedule.status === 'sending' ? 'info' : 'danger'
                    }`}>
                      {schedule.status}
                    </span>
                  </td>
                  <td>{new Date(schedule.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhatsAppStatus;
